"use client";
import {
  Binary,
  Copy,
  Eye,
  PackageSearch,
  Terminal,
} from "lucide-react";
import {
  useState,
} from "react";
import {
  type ScanReport,
} from "../../../lib/types";
import {
  formatBytes,
} from "../../shared/format";


export function PayloadsView({
  report,
  onOpenEntry,
  onCopy,
}: {
  report: ScanReport;
  onOpenEntry: (path: string) => void;
  onCopy: (value: string, message?: string) => void;
}) {
  const [selectedId, setSelectedId] = useState(
    report.embeddedPayloads[0]?.id ?? "",
  );
  const selected =
    report.embeddedPayloads.find((payload) => payload.id === selectedId) ??
    report.embeddedPayloads[0] ??
    null;
  return (
    <div className="payload-layout">
      <section className="panel payload-list-panel">
        <div className="panel-heading">
          <div>
            <div className="section-kicker">SECOND-STAGE CONTENT</div>
            <h3>Embedded payloads</h3>
          </div>
          <span>{report.embeddedPayloads.length} inspected</span>
        </div>
        <div className="payload-list">
          {report.embeddedPayloads.map((payload) => (
            <button
              type="button"
              key={payload.id}
              className={selected?.id === payload.id ? "is-selected" : ""}
              onClick={() => setSelectedId(payload.id)}
            >
              <span className={`payload-icon risk-${payload.risk}`}>
                {payload.executable ? (
                  <Terminal size={17} />
                ) : (
                  <Binary size={17} />
                )}
              </span>
              <span>
                <strong>{payload.path.split("/").pop()}</strong>
                <small>
                  {payload.type} · {payload.architecture ?? "unknown arch"} ·{" "}
                  {formatBytes(payload.size)}
                </small>
                <code>{payload.path}</code>
              </span>
              <i className={`risk-pill risk-${payload.risk}`}>
                {payload.risk}
              </i>
            </button>
          ))}
          {!report.embeddedPayloads.length && (
            <div className="empty-panel">
              <Binary size={27} />
              <strong>No embedded native or script payloads</strong>
              <span>
                Nested JARs and ordinary resources remain available in Explorer.
              </span>
            </div>
          )}
        </div>
      </section>
      <section className="panel payload-detail-panel">
        {selected ? (
          <>
            <header>
              <div>
                <div className="section-kicker">STRUCTURAL INSPECTION</div>
                <h2>{selected.path.split("/").pop()}</h2>
                <code>{selected.path}</code>
              </div>
              <button
                className="secondary-button compact"
                type="button"
                onClick={() => onOpenEntry(selected.path)}
              >
                <Eye size={14} />
                Inspect bytes
              </button>
            </header>
            <div className="payload-facts">
              <div>
                <span>FORMAT</span>
                <strong>{selected.type}</strong>
              </div>
              <div>
                <span>ARCHITECTURE</span>
                <strong>{selected.architecture ?? "Unknown"}</strong>
              </div>
              <div>
                <span>ENTROPY</span>
                <strong>{selected.entropy.toFixed(2)}</strong>
              </div>
              <div>
                <span>PACKED</span>
                <strong>{selected.packed ? "Likely" : "No marker"}</strong>
              </div>
            </div>
            <div className="payload-hash">
              <span>SHA-256</span>
              <button
                type="button"
                onClick={() => onCopy(selected.sha256, "Payload hash copied")}
              >
                <code>{selected.sha256}</code>
                <Copy size={13} />
              </button>
            </div>
            <section>
              <h3>Detected capabilities</h3>
              <div className="payload-capabilities">
                {selected.capabilities.map((capability) => (
                  <span key={capability}>{capability}</span>
                ))}
                {!selected.capabilities.length && (
                  <p>No high-signal capability strings were recovered.</p>
                )}
              </div>
            </section>
            <section>
              <h3>Indicators</h3>
              <div className="payload-indicators">
                {selected.indicators.map((indicator) => (
                  <code key={indicator}>{indicator}</code>
                ))}
                {!selected.indicators.length && <p>No network IOC recovered.</p>}
              </div>
            </section>
            <section>
              <h3>Suspicious strings</h3>
              <div className="payload-string-list">
                {selected.suspiciousStrings.slice(0, 30).map((value) => (
                  <code key={value}>{value}</code>
                ))}
              </div>
            </section>
          </>
        ) : (
          <div className="entry-empty">
            <PackageSearch size={34} />
            <strong>No payload selected</strong>
            <span>JarLab never executes embedded content.</span>
          </div>
        )}
      </section>
    </div>
  );
}
